import React, { createContext, useEffect, useMemo, useState } from 'react'
import { UserType } from 'src/lib/types/user'
import { getParsedUserObj } from 'src/lib/utils/user'
import { constants } from '../utils/constants'
import Cookies from 'js-cookie'
import { staticOptions } from '../core/api-client'
import useSWR from 'swr'
import { apiURI } from '../utils/constants'
import { fetcher } from '../core/api-client'
import { getActiveStore } from '../utils/onboarding'
import { useOnboarding } from './OnboardingContext'
import { useRouter } from 'next/router'
import { hasAuthCookie, hasRefreshCookie } from '../utils/helpers'

interface UserContextType {
  setUserCtx: (data: any) => void
  user: UserType | undefined
  canAccess: (value: string | string[], quantity: number) => boolean
  hasAllTabPermissions: (tabName: string) => boolean
  isUserProfileLoading: boolean
  /** True during initial profile fetch before permissions are in React state. */
  showNavSkeleton: boolean
  isUserConfigured: boolean
  mutateUser: () => Promise<unknown>
}

const UserContext = createContext({} as UserContextType)

const UserProvider = (props: any) => {
  const router = useRouter()
  const [user, setUser] = useState<UserType>()

  const {
    data: userResponse,
    isValidating,
    isLoading,
    mutate: mutateUser
  } = useSWR<any>(
    '/merchant-user/profile',
    async url => {
      // Read cookie state at fetch time, not render time: `loggedIn` is a render-time
      // snapshot, so a `mutateUser()` fired right after login() (which only writes
      // cookies, without re-rendering providers) would otherwise re-run a stale
      // closure and cache null again.
      if (!hasAuthCookie() && !hasRefreshCookie()) {
        return null
      }
      const response = await fetcher(url, apiURI.AUTH_ENGINE)
      return response
    },
    staticOptions
  )

  useEffect(() => {
    if (isValidating || !userResponse) return
    const { data } = userResponse as any
    setUserCtx(data)
    const parsedData = getParsedUserObj(data)
    setUser(parsedData)
  }, [isValidating, userResponse])

  const setUserCtx = (data: any) => {
    const parsedData = getParsedUserObj(data)
    Cookies.set(
      constants.FLO_PROFILE_DATA,
      JSON.stringify({
        sortmentTrackingId: parsedData?.sortmentTrackingId
      }),
      {
        expires: constants.FLO_USER_COOKIE_EXPIRY,
        sameSite: 'none',
        secure: true
      }
    )
    setUser(parsedData)
  }

  const canAccess = (value: string | string[], quantity = 0) => {
    if (!value) return false
    let hasPermission: any
    // In case the required permissions is coming up as an array:
    if (Array.isArray(value)) {
      hasPermission = user?.permissions?.find((permission: any) => value.includes(permission.id))
    } else {
      hasPermission = user?.permissions?.find((permission: any) => permission.id === value)
    }
    const limit = hasPermission?.metadata?.limit
    if (limit) {
      return Boolean(limit > quantity)
    }
    return Boolean(hasPermission)
  }

  const hasAllTabPermissions = (tabName: string) => {
    if (!tabName) return false

    const hasPermission = user?.permissions?.find((permission: any) => permission.id === tabName)

    if (!hasPermission) return false
    const totalTabPermissions = hasPermission.totalChildPermissions
    const availableTabPermissions = user?.permissions?.filter(
      (permission: any) => permission.parentPermissionId === tabName
    )
    return Boolean(totalTabPermissions === availableTabPermissions?.length)
  }

  const isUserConfigured = useMemo(() => {
    return Boolean(user?.firstName) && Boolean(user?.phoneNumber) && Boolean(user?.role)
  }, [user])

  const showNavSkeleton = useMemo(
    () => (isLoading || !user) && !user?.permissions?.length,
    [isLoading, user]
  )


  const userContextValue: UserContextType = {
    setUserCtx,
    user,
    canAccess,
    hasAllTabPermissions,
    isUserProfileLoading: isLoading,
    showNavSkeleton,
    isUserConfigured,
    mutateUser
  }

  return <UserContext.Provider value={userContextValue} {...props} />
}

const useUser = () => React.useContext(UserContext)

export { UserProvider, useUser }
